import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Shell, KpiCard, Section, StatusPill } from "@/components/loomi/Layout";
import { Link } from "react-router-dom";
import { LayoutDashboard, FolderKanban, Users, Building2, ScrollText, Gift, Upload, Settings, Plug, Cable, Plus } from "lucide-react";

import { UserCircle } from "lucide-react";
const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, testId: "nav-admin-overview" },
  { to: "/admin/projects", label: "Projects", icon: FolderKanban, testId: "nav-admin-projects", match: "/admin/projects" },
  { to: "/pm/new", label: "New redirect project", icon: Plus, testId: "nav-admin-new-project" },
  { to: "/admin/bulk-upload", label: "Bulk upload", icon: Upload, testId: "nav-admin-bulk-upload" },
  { to: "/admin/clients", label: "Clients", icon: Building2, testId: "nav-admin-clients" },
  { to: "/admin/partners", label: "Partners (outbound)", icon: Cable, testId: "nav-admin-partners", match: "/admin/partners" },
  { to: "/admin/vendors", label: "Vendors (inbound)", icon: Plug, testId: "nav-admin-vendors", match: "/admin/vendors" },
  { to: "/admin/respondents", label: "Respondents", icon: Users, testId: "nav-admin-respondents" },
  { to: "/admin/rewards", label: "Rewards & Payouts", icon: Gift, testId: "nav-admin-rewards" },
  { to: "/admin/logs", label: "Redirect logs", icon: ScrollText, testId: "nav-admin-logs" },
  { to: "/admin/settings", label: "Settings", icon: Settings, testId: "nav-admin-settings" },
  { to: "/admin/profile", label: "Profile", icon: UserCircle, testId: "nav-admin-profile" },
];

export default function AdminDashboard() {
  const [kpis, setKpis] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    (async () => {
      const [k, p] = await Promise.all([api.get("/admin/kpis"), api.get("/projects")]);
      setKpis(k.data); setProjects(p.data);
    })();
  }, []);

  return (
    <Shell title="Overview" portalLabel="Admin" nav={NAV}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Live projects" value={kpis?.projects_live ?? "—"} accent="primary" testId="kpi-projects-live" />
        <KpiCard label="Starts" value={kpis?.starts ?? "—"} accent="blue" testId="kpi-starts" />
        <KpiCard label="Completes" value={kpis?.complete ?? "—"} accent="green" testId="kpi-complete" />
        <KpiCard label="Terminates" value={kpis?.terminate ?? "—"} accent="red" testId="kpi-terminate" />
        <KpiCard label="Overquotas" value={kpis?.overquota ?? "—"} accent="amber" testId="kpi-overquota" />
        <KpiCard label="Conversion" value={`${kpis?.conversion_pct ?? "—"}%`} accent="green" testId="kpi-conversion" />
        <KpiCard label="Respondents" value={kpis?.respondents ?? "—"} accent="primary" testId="kpi-respondents" />
        <KpiCard label="Clients" value={kpis?.clients ?? "—"} accent="blue" testId="kpi-clients" />
      </div>

      <div className="mt-8">
        <Section title="Recent projects" action={<Link to="/admin/projects" className="text-xs text-mute hover:text-white" data-testid="admin-view-all-projects">View all →</Link>} testId="section-recent-projects">
          <ProjectsTable projects={projects.slice(0, 8)} basePath="/admin/projects" />
        </Section>
      </div>
    </Shell>
  );
}

export function ProjectsTable({ projects, basePath, selectable = false, selectedIds = [], onToggle, onToggleAll, clientMap = {} }) {
  if (!projects?.length) return <div className="text-mute text-sm py-8 text-center" data-testid="projects-empty">No projects yet.</div>;
  const allSelected = selectable && projects.length > 0 && projects.every((p) => selectedIds.includes(p.id));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="projects-table">
        <thead>
          <tr className="text-left text-xs uppercase tracking-widest text-mute border-b border-white/10">
            {selectable && (
              <th className="py-3 pr-3 w-8">
                <input
                  type="checkbox" checked={allSelected}
                  onChange={() => onToggleAll?.(!allSelected)}
                  data-testid="projects-select-all"
                  className="rounded border-white/20 bg-black/40"
                  aria-label="Select all"
                />
              </th>
            )}
            <th className="py-3 pr-4">Name</th>
            <th className="py-3 pr-4">Client</th>
            <th className="py-3 pr-4">Type</th><th className="py-3 pr-4">Status</th>
            <th className="py-3 pr-4 text-right">Starts</th><th className="py-3 pr-4 text-right">Complete</th>
            <th className="py-3 pr-4 text-right">Term</th><th className="py-3 pr-4 text-right">CPI</th><th className="py-3" />
          </tr>
        </thead>
        <tbody>
          {projects.map((p) => {
            const isSel = selectedIds.includes(p.id);
            return (
              <tr key={p.id} className={`border-b border-white/5 hover:bg-white/5 ${isSel ? "bg-indigo-500/5" : ""}`} data-testid={`project-row-${p.id}`}>
                {selectable && (
                  <td className="py-3 pr-3">
                    <input
                      type="checkbox" checked={isSel}
                      onChange={() => onToggle?.(p.id)}
                      data-testid={`project-select-${p.id}`}
                      className="rounded border-white/20 bg-black/40"
                      aria-label={`Select ${p.name}`}
                    />
                  </td>
                )}
                <td className="py-3 pr-4">
                  <Link to={`${basePath}/${p.id}`} className="font-medium hover:underline" data-testid={`project-link-${p.id}`}>{p.name}</Link>
                  <div className="text-[10px] text-mute font-mono mt-0.5">{p.id.slice(0,8)}</div>
                </td>
                <td className="py-3 pr-4 text-xs text-mute">{clientMap[p.client_id] || "—"}</td>
                <td className="py-3 pr-4"><span className="text-xs uppercase tracking-widest text-mute">{p.type}</span></td>
                <td className="py-3 pr-4"><StatusPill status={p.status} /></td>
                <td className="py-3 pr-4 text-right font-mono">{p.counters?.starts ?? 0}</td>
                <td className="py-3 pr-4 text-right font-mono status-complete">{p.counters?.complete ?? 0}</td>
                <td className="py-3 pr-4 text-right font-mono status-terminate">{p.counters?.terminate ?? 0}</td>
                <td className="py-3 pr-4 text-right font-mono">${(p.cpi || 0).toFixed(2)}</td>
                <td className="py-3 text-right"><Link className="text-xs text-indigo-300 hover:underline" to={`${basePath}/${p.id}`} data-testid={`project-open-${p.id}`}>Open →</Link></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export { NAV as ADMIN_NAV };
